import { useState, useCallback } from 'react';
import { format } from 'date-fns';
import { MapPin, Repeat, Ticket } from 'lucide-react';
import { CalendarEvent } from '../../types/calendar';
import EventTooltip from './EventTooltip';
import { useTicketLimit } from '../../hooks/useTicketLimit';
import { decodeHtmlEntities } from '../../utils/security';

function isTouchDevice() {
  return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
}

interface EventChicletProps {
  event: CalendarEvent;
  showLocation?: boolean;
  calendarId: string;
  color?: string;
  isFirstDay?: boolean;
  isLastDay?: boolean;
  isMiddleDay?: boolean;
  isPaid?: boolean;
  isSubscribed?: boolean;
}

export default function EventChiclet({
  event,
  showLocation = false,
  calendarId,
  color = '#7C3AED',
  isFirstDay = true,
  isLastDay = true,
  isMiddleDay = false,
  isPaid = false,
  isSubscribed = false
}: EventChicletProps) {
  const [showModal, setShowModal] = useState(false);
  const hasLocation = !!event.location || event.description?.includes('Location:');
  const { ticketLimit, soldTickets } = useTicketLimit(calendarId, event.id);

  const remainingTickets = ticketLimit ? ticketLimit - soldTickets : null;
  const isSoldOut = ticketLimit && remainingTickets <= 0;

  const handleOpen = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setShowModal(true);
  }, []);

  const getEventStyles = () => {
    const baseStyles = {
      backgroundColor: `${color}15`,
      color,
      borderColor: color,
    };

    if (isFirstDay) {
      return {
        ...baseStyles,
        borderLeft: `4px solid ${color}`,
        borderTop: `1px solid ${color}`,
        borderBottom: `1px solid ${color}`,
        borderRight: isLastDay ? `1px solid ${color}` : 'none',
        borderTopLeftRadius: '0.375rem',
        borderBottomLeftRadius: '0.375rem',
        borderTopRightRadius: isLastDay ? '0.375rem' : '0',
        borderBottomRightRadius: isLastDay ? '0.375rem' : '0',
      };
    } else if (isLastDay) {
      return {
        ...baseStyles,
        borderTop: `1px solid ${color}`,
        borderBottom: `1px solid ${color}`,
        borderRight: `1px solid ${color}`,
        borderTopRightRadius: '0.375rem',
        borderBottomRightRadius: '0.375rem',
      };
    } else if (isMiddleDay) {
      return {
        ...baseStyles,
        borderTop: `1px solid ${color}`,
        borderBottom: `1px solid ${color}`,
        marginLeft: '-0.5rem',
        marginRight: '-0.5rem',
      };
    }

    return baseStyles;
  };

  return (
    <div className="relative">
      <div
        className="px-2 py-1 text-xs transition-colors cursor-pointer group relative"
        style={getEventStyles()}
        onClick={isTouchDevice() ? undefined : handleOpen}
        onTouchEnd={isTouchDevice() ? handleOpen : undefined}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setShowModal(true); } }}
      >
        <div className="flex items-center gap-2 max-w-full overflow-hidden">
          {isFirstDay ? (
            <>
              <span className="font-medium whitespace-nowrap flex-shrink-0">
                {format(event.start, 'h:mm a')}
              </span>
              <span className="truncate flex-1 min-w-0 flex items-center gap-1">
                {decodeHtmlEntities(event.title)}
                {event.isRecurring && (
                  <Repeat className="h-3 w-3 flex-shrink-0 opacity-75" />
                )}
                {ticketLimit && (
                  <Ticket className="h-3 w-3 flex-shrink-0 opacity-75" />
                )}
                {isSoldOut && (
                  <span className="text-red-500 font-medium ml-1">SOLD OUT</span>
                )}
              </span>
              {hasLocation && showLocation && !isPaid && (
                <MapPin className="h-3 w-3 flex-shrink-0" />
              )}
            </>
          ) : isMiddleDay ? (
            <span className="truncate flex-1 min-w-0 opacity-75 flex items-center gap-1">
              {decodeHtmlEntities(event.title)}
              {event.isRecurring && (
                <Repeat className="h-3 w-3 flex-shrink-0 opacity-75" />
              )}
            </span>
          ) : (
            <>
              <span className="font-medium whitespace-nowrap flex-shrink-0">
                {format(event.end, 'h:mm a')}
              </span>
              <span className="truncate flex-1 min-w-0 flex items-center gap-1">
                {decodeHtmlEntities(event.title)}
                {event.isRecurring && (
                  <Repeat className="h-3 w-3 flex-shrink-0 opacity-75" />
                )}
              </span>
            </>
          )}
        </div>
      </div>

      {showModal && (
        <EventTooltip
          event={event}
          showLocation={showLocation && (!isPaid || isSubscribed)}
          onClose={() => setShowModal(false)}
          ticketLimit={ticketLimit}
          remainingTickets={remainingTickets}
          isPaid={isPaid}
          isSubscribed={isSubscribed}
          isMobile={isTouchDevice()}
        />
      )}
    </div>
  );
}
